import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { Loader } from 'cdk-radial';
import { AppMetrics } from '@cdk-prod/fortellis-api-metrics-component'
import {
    useAppMetricsFetch,
    useAppFetch,
    useAsyncApiEventsForAppFetch
} from '../../utils/fetchMetricsContext';
import { environmentURLs } from '../../../common/CaptureEnvironment';
import {
    TIME_SPANS,
    LATENCIES,
    API_ENVS_FOR_APPS,
    ALL_APIS_OPTION,
    API_STATUS
} from '../../utils/metricsUtil';
import { APP_METRICS_THEME } from "../../../../constants/constants";
import { getOrders } from "../../../../redux/connectedComponent/orderAPI/orderAPI.slice";
const APIGATEWAY_BASE_URL = environmentURLs.REACT_APP_API_GATEWAY_DIRECTORY_URL;
const ASYNCAPI_BASE_URL = environmentURLs.REACT_APP_ASYNC_API_SERVICE;
const appMetricsTheme = APP_METRICS_THEME.DEALER_APP_METRICS

const Metrics = () => {
    const state = useSelector((state) => state);
    const dispatch = useDispatch();
    const { fortellisEnterpriseEntityId, authenticationToken: token } =
        state?.loginDetailsSlice;
    const auth = {
        accessToken: state?.loginDetailsSlice.authenticationToken,
        isAuthenticated: state?.loginDetailsSlice.isAuthenticated
    };
    const appId = state?.orderAPISlice?.orders?.app?.id;

    const [filters, setFilters] = useState({
        timeRange: TIME_SPANS[0].value,
        latencies: [LATENCIES[0].value],
        apiId: ALL_APIS_OPTION.value,
        environment: API_ENVS_FOR_APPS[0].value,
        kind: undefined
    });
    const [apiOptions, setApiOptions] = useState([]);
    const [asyncCardsShow, setAsyncCardsShow] = useState(false);
    const [asyncMetric, setAsyncMetric] = useState([]);
    const [ordersLoaded, setOrdersLoaded] = useState(false);

    useEffect(() => {
        if (fortellisEnterpriseEntityId) {
            dispatch(getOrders({ token, entityId: fortellisEnterpriseEntityId }))
                .then(() => setOrdersLoaded(true));
        }
    }, [fortellisEnterpriseEntityId, dispatch, token]);

    const [
        appInfoIsLoading,
        isAppInfoRequestFulfilled,
        appInfoError,
        appInfoData,
        fetchApp
    ] = useAppFetch({});

    const [
        appDetailMetricsIsLoading,
        isAppDetailMetricsRequestFulfilled,
        appDetailMetricsError,
        appDetailMetricsResponse,
        fetchAppDetailMetrics
    ] = useAppMetricsFetch();

    const [
        asyncEventIsLoading,
        isAsyncEventRequestFulfilled,
        asyncEventError,
        asyncEventResponse,
        fetchAsyncEvents
    ] = useAsyncApiEventsForAppFetch();


    useEffect(
        function () {
            if (auth.isAuthenticated && !!appId) {
                fetchApp(auth.accessToken || '', appId);
            }
        },
        [auth.isAuthenticated, auth.accessToken, appId]
    );

    useEffect(() => {
        !!asyncEventResponse && setAsyncMetric(asyncEventResponse);
    }, [asyncEventResponse]);

    useEffect(() => {
        if (auth.isAuthenticated && !!appId) {
            fetchAppDetailMetrics(appId, auth.accessToken || '', filters);
        }
    },
        [auth.isAuthenticated, auth.accessToken, appId]
    );

    useEffect(() => {
        if (auth.isAuthenticated && asyncCardsShow && !!appId) {
            fetchAsyncEvents(appId, auth.accessToken || '', filters);
        }
    }, [auth.isAuthenticated, asyncCardsShow, appId]);

    useEffect(() => {
        if (!!appInfoData) {
            getAllApis(appInfoData.asyncApiIntegrations, appInfoData.apiProducts);
        }
    }, [appInfoData]);


    async function getAllApis(asyncApiIntegrations, apiProducts) {
        let promiseResolvedApi = [];
        let promiseRejectedApi = [];
        let instanceId = '';
        if (!!apiProducts && apiProducts.length) {
            await Promise.allSettled(
                apiProducts.map(apiId => {
                    const id = apiId.replace('api-v2-', '');
                    return axios.get(
                        `${APIGATEWAY_BASE_URL}/directory/apis/${id}`,
                        { headers: { Authorization: `Bearer ${auth.accessToken}` } }
                    );
                })
            ).then(results => {
                results.forEach(result => {
                    result.status === 'fulfilled'
                        ? promiseResolvedApi.push(result.value.data)
                        : promiseRejectedApi.push(result.reason);
                });
            });
            prepareApiDropdownOption(promiseResolvedApi);
        }
        if (!!asyncApiIntegrations && asyncApiIntegrations.length > 0) {
            let filterAsyncApi =
                asyncApiIntegrations.filter(item => {
                    return item.asyncApi?.status === API_STATUS.ACTIVE;
                }) || [];
            filterAsyncApi.length > 0 &&
                (await Promise.allSettled(
                    filterAsyncApi.map(api => {
                        instanceId = api?.asyncApi?.id;
                        return axios.get(
                            `${ASYNCAPI_BASE_URL}/v2/directory/async-apis/${instanceId}`,
                            { headers: { Authorization: `Bearer ${auth.accessToken}` } }
                        );
                    })
                ).then(results => {
                    results.forEach(result => {
                        result.status === 'fulfilled'
                            ? promiseResolvedApi.push(result.value.data)
                            : promiseRejectedApi.push(result.reason);
                    });
                }));
            prepareApiDropdownOption(promiseResolvedApi);
        }
    }
    const prepareApiDropdownOption = apis => {
        let apisOptions = [];
        apis.forEach(api => {
            const obj = {};
            obj['label'] = api.name;
            obj['value'] = api.id;
            obj['kind'] = api.kind === 'api' ? 'rest' : 'async';
            apisOptions.push(obj);
        });
        setApiOptions(apisOptions);
    };


    return (

        <div>
            {(!ordersLoaded || apiOptions.length == 0) ? (
                <div className="api-details-loading-provisiong">
                    <Loader />
                    <div className="loader-text">
                        <p>Fetching Metrics Report...</p>
                    </div>
                </div>
            ) : (
                <AppMetrics
                    auth={auth}
                    belongsTo={appMetricsTheme}
                    appId={appId}
                    apiOptions={apiOptions}
                    appInfoIsLoading={appInfoIsLoading}
                    isAppInfoRequestFulfilled={isAppInfoRequestFulfilled}
                    appDetailMetricsIsLoading={appDetailMetricsIsLoading}
                    isAppDetailMetricsRequestFulfilled={isAppDetailMetricsRequestFulfilled}
                    appDetailMetricsError={appDetailMetricsError}
                    appDetailMetricsResponse={appDetailMetricsResponse}
                    fetchAppDetailMetrics={fetchAppDetailMetrics}
                    asyncEventIsLoading={asyncEventIsLoading}
                    isAsyncEventRequestFulfilled={isAsyncEventRequestFulfilled}
                    asyncEventError={asyncEventError}
                    asyncEventResponse={asyncEventResponse}
                    fetchAsyncEvents={fetchAsyncEvents}
                />)}
        </div>

    )
}

export default Metrics;